import React, { useState, useEffect, useLayoutEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Text } from "@hangar/web-components/core/Text";
import { Spinner } from '@hangar/web-components/core/Spinner';
import { Alert } from '@ecargo/web-components';
import { useFlightChangeCases } from '../../hooks/flightCases/useFlightChangeCases';
import { useAssignment } from '../../hooks/flightCases/useAssignment';
import { AssignmentButton } from '../../components/common';
import * as S from './FlightChangesCases.styles';
import { useTranslation } from "../../hooks/i18n";
import { formatDateTime, formatDateTimeWithTimezone } from '../../utils/date';
import { NotificationStatus, FlightChangeCase } from '../../services/api/flightChanges';
import { ErrorMessage } from './ErrorMessage';
import { FlightChangesCasesTableEmpty } from './FlightChangesCasesTableEmpty';
import { FlightChangesCasesClearFiltersButton } from './FlightChangesCasesClearFiltersButton';
import { FlightChangesCasesSkeleton } from './FlightChangesCasesSkeleton';
import { FlightChangesCasesAwbFilter } from './FlightChangesCasesAwbFilter';
import { FlightChangesCasesAnalystFilter } from './FlightChangesCasesAnalystFilter';
import { FlightChangesCasesStatusFilter } from './FlightChangesCasesStatusFilter';
import { FlightChangesCasesTableFrame, type FlightChangesCasesSortableColumn } from './FlightChangesCasesTableFrame';
import { FlightChangesCasesTablePagination } from './FlightChangesCasesTablePagination';
import { ChevronRightOutlined } from '@hangar/react-icons/core/interaction/ChevronRightOutlined';
import { StatusChip } from '../../components/common/StatusChip';
import { ErrorSnackbar } from './ErrorSnackbar';
import type { FlightChangeDetailLocationState } from '../../router/routes';
import { scrollViewportAndAncestorsToTop } from '../../utils/scrollViewportAndAncestorsToTop';

interface LocationState {
    successMessage?: string;
    successMessageBold?: string;
    errorMessage?: string;
    errorMessageBold?: string;
    warningMessage?: string;
    warningMessageBold?: string;
}

const TIMEZONE_LABEL_REFERENCE_DATE = '2025-01-01T00:00:00Z';

const ROWS_PER_PAGE_OPTIONS = [10, 20, 50];

const SORTABLE_COLUMNS: FlightChangesCasesSortableColumn[] = [
    { sortBy: 'loadDate', labelKey: 'flightChangesCases.registration' },
    { sortBy: 'awb', labelKey: 'flightChangesCases.awb' },
    { sortBy: 'route', labelKey: 'flightChangesCases.route' },
    { sortBy: 'movementNumber', labelKey: 'flightChangesCases.movement' },
    { sortBy: 'notificationStatus', labelKey: 'flightChangesCases.notification' },
    { sortBy: 'analyst', labelKey: 'flightChangesCases.analyst' },
    { sortBy: 'status', labelKey: 'flightChangesCases.status' },
];

const tableColSpan = SORTABLE_COLUMNS.length + 1;

/** Bandeja — eCargo `Alert` para banner de error de lista/carga; snackbar Hangar vía `ErrorSnackbar`. Ver `ECADCA-530-review.md` §2. */
export const FlightChangesCases = () => {
    const { flights, loading, hasCompletedInitialFetch, error, refetch, updateFlightAnalyst, total, page, pageSize, setPage, setPageSize, sortBy, sortOrder, handleRequestSort, isSorting, isCaseReadOnly, selectedStatusFilters, onStatusFilterChange, awbFilterInput, onAwbFilterChange, selectedAnalystId, filterUnassigned, analystOptions, onAnalystFilterChange, hasActiveFilters, clearAllFilters } = useFlightChangeCases();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const listRootRef = React.useRef<HTMLDivElement>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    const [successMessageBold, setSuccessMessageBold] = useState<string | null>(null);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [errorMessageBold, setErrorMessageBold] = useState<string | null>(null);
    const [warningMessage, setWarningMessage] = useState<string | null>(null);
    const [warningMessageBold, setWarningMessageBold] = useState<string | null>(null);

    const { handleAssign, conflictError, genericError, clearError, isAssigning } = useAssignment({
        onSuccess: refetch,
        onOptimisticUpdate: updateFlightAnalyst,
        onError: refetch,
    });

    const replaceLocationClearingState = React.useCallback(() => {
        navigate(
            { pathname: location.pathname, search: location.search, hash: location.hash },
            { replace: true, state: {} },
        );
    }, [navigate, location.pathname, location.search, location.hash]);

    /**
     * Tras enviar el formulario en detalle, la lista vuelve con mensaje en `state`.
     * El shell eCargo hace scroll en un panel `overflow-y: auto`, no solo en `window`.
     */
    useLayoutEffect(() => {
        const state = location.state as LocationState | null;
        if (
            !state?.successMessage &&
            !state?.errorMessage &&
            !state?.warningMessage
        ) {
            return;
        }
        const run = () => scrollViewportAndAncestorsToTop(listRootRef.current);
        run();
        requestAnimationFrame(run);
        requestAnimationFrame(() => requestAnimationFrame(run));
    }, [location]);

    useEffect(() => {
        const state = location.state as LocationState | null;
        if (state?.successMessage) {
            setSuccessMessage(state.successMessage);
            setSuccessMessageBold(state.successMessageBold ?? null);
            replaceLocationClearingState();
            setTimeout(() => {
                setSuccessMessage(null);
                setSuccessMessageBold(null);
            }, 5000);
        }
        if (state?.errorMessage) {
            setErrorMessage(state.errorMessage);
            setErrorMessageBold(state.errorMessageBold ?? null);
            replaceLocationClearingState();
            setTimeout(() => {
                setErrorMessage(null);
                setErrorMessageBold(null);
            }, 5000);
        }
        if (state?.warningMessage) {
            setWarningMessage(state.warningMessage);
            setWarningMessageBold(state.warningMessageBold ?? null);
            replaceLocationClearingState();
            setTimeout(() => {
                setWarningMessage(null);
                setWarningMessageBold(null);
            }, 5000);
        }
    }, [location, replaceLocationClearingState]);

    const handleStatusClick = (flightChangeId: number) => {
        const listReturnTo = `${location.pathname}${location.search}`;
        navigate(`/flight-change-cases/${flightChangeId}`, {
            state: { flightCasesListReturnTo: listReturnTo } satisfies FlightChangeDetailLocationState,
        });
    };

    const handleChangePage = (newPage: number) => setPage(newPage);
    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const value = Number.parseInt(event.target.value, 10);
        setPageSize(Number.isNaN(value) ? 10 : value);
    };

    const registrationTimezoneLabel = useMemo(() => {
        const { timeZoneLabel } = formatDateTimeWithTimezone(TIMEZONE_LABEL_REFERENCE_DATE);
        return timeZoneLabel || t('flightChangesCases.localTime');
    }, [t]);

    const tableTitle = t('flightChangesCases.title');

    const tableFrameCommon = {
        tableAriaLabel: tableTitle,
        sortBy,
        sortOrder,
        columns: SORTABLE_COLUMNS,
        t,
    } as const;

    if (loading && !isSorting && !hasCompletedInitialFetch) {
        return (
            <S.Container ref={listRootRef}>
                <FlightChangesCasesSkeleton />
            </S.Container>
        );
    }

    const renderRegistration = (flight: FlightChangeCase) => {
        const { date, time } = formatDateTime(flight.loadDate);
        return (
            <S.DateCell>
                <Text variant="body-md">{date}</Text>
                <S.RegistrationTimeText><Text variant="body-sm" color="secondary">{time}</Text></S.RegistrationTimeText>
            </S.DateCell>
        );
    };

    const renderNotification = (flight: FlightChangeCase) => {
        if (!flight.notificationStatus) return <Text variant="body-md">-</Text>;
        if (flight.notificationStatus === NotificationStatus.AT_RISK) {
            return (
                <S.NotificationContainer>
                    <Text variant="body-md" bold={true}>{t(flight.notificationStatus)}</Text>
                    <S.RiskDot />
                </S.NotificationContainer>
            );
        }
        return <Text variant="body-md">{t(flight.notificationStatus)}</Text>;
    };

    return (
        <>
        <S.FilterDropdownOptionHoverStyles />
        <S.Container ref={listRootRef}>
            <Text variant="heading-lg" bold={true}>{t("flightChangesCases.title")}</Text>
            {successMessage && (
                <S.AlertWrapper>
                <Alert
                    description={successMessageBold 
                        ? `${successMessage} ${successMessageBold}` 
                        : successMessage
                    }
                    variant="success"
                    onClose={() => {
                        setSuccessMessage(null);
                        setSuccessMessageBold(null);
                    }}
                />
                </S.AlertWrapper>
            )}
            {errorMessage && (
                <S.AlertWrapper>
                <Alert
                    description={errorMessageBold 
                        ? `${errorMessage} ${errorMessageBold}` 
                        : errorMessage
                    }
                    variant="error"
                    onClose={() => {
                        setErrorMessage(null);
                        setErrorMessageBold(null);
                    }}
                />
                </S.AlertWrapper>
            )}
            {warningMessage && (
                <S.AlertWrapper>
                <Alert
                    description={warningMessageBold 
                        ? `${warningMessage} ${warningMessageBold}` 
                        : warningMessage
                    }
                    variant="warning"
                    onClose={() => {
                        setWarningMessage(null);
                        setWarningMessageBold(null);
                    }}
                />
                </S.AlertWrapper>
            )}
            <S.Card>
                <S.Header>
                    <S.SubtitleTitle><Text variant="heading-md" color="primary" bold={false}>{t("flightChangesCases.subtitle")}</Text></S.SubtitleTitle>
                    <S.RegistrationTimezoneText><Text variant="body-md" color="secondary">{t('flightChangesCases.registrationTimezoneMessage', { timezone: registrationTimezoneLabel })}</Text></S.RegistrationTimezoneText>
                </S.Header>

                <S.FiltersRow>
                    <S.FilterInputsShell>
                        <FlightChangesCasesAwbFilter
                            value={awbFilterInput}
                            onChange={onAwbFilterChange}
                            labelKey="flightChangesCases.awb"
                            placeholderKey="flightChangesCases.filters.awbPlaceholder"
                            t={t}
                        />
                        <FlightChangesCasesStatusFilter
                            value={selectedStatusFilters}
                            onChange={onStatusFilterChange}
                            t={t}
                        />
                        <FlightChangesCasesAnalystFilter
                            analystId={selectedAnalystId}
                            filterUnassigned={filterUnassigned}
                            options={analystOptions}
                            onChange={onAnalystFilterChange}
                            labelKey="flightChangesCases.analyst"
                            placeholderKey="flightChangesCases.filters.analystPlaceholder"
                            unassignedOptionKey="flightChangesCases.filters.sinAnalizar"
                            t={t}
                        />
                    </S.FilterInputsShell>
                    <S.FilterActionsColumn>
                        <S.FilterActionsLabelSpacer aria-hidden="true">{'\u00a0'}</S.FilterActionsLabelSpacer>
                        <FlightChangesCasesClearFiltersButton
                            disabled={!hasActiveFilters}
                            onClick={clearAllFilters}
                            label={t('flightChangesCases.filters.clearAll')}
                        />
                    </S.FilterActionsColumn>
                </S.FiltersRow>

                { error && <ErrorMessage /> }
                { !error && !loading && flights.length === 0 && (
                    <FlightChangesCasesTableFrame {...tableFrameCommon} onSortRequest={handleRequestSort}>
                        <S.DataTableRow>
                            <S.DataTableCell
                                colSpan={tableColSpan}
                                style={{ border: 'none', verticalAlign: 'middle' }}
                            >
                                <FlightChangesCasesTableEmpty variant={hasActiveFilters ? 'filtered' : 'global'} />
                            </S.DataTableCell>
                        </S.DataTableRow>
                    </FlightChangesCasesTableFrame>
                ) }
                { !error && loading && isSorting && (
                    <FlightChangesCasesTableFrame {...tableFrameCommon}>
                        <S.DataTableRow>
                            <S.DataTableCell
                                colSpan={tableColSpan}
                                style={{ border: 'none', verticalAlign: 'middle' }}
                            >
                                <S.LoadingContainer data-testid="flight-changes-sort-spinner">
                                    <Spinner size="normal" />
                                    <S.LoadingText>{t('flightChangesCases.loadingSortMessage')}</S.LoadingText>
                                </S.LoadingContainer>
                            </S.DataTableCell>
                        </S.DataTableRow>
                    </FlightChangesCasesTableFrame>
                ) }
                { !error && loading && !isSorting && hasCompletedInitialFetch && (
                    <FlightChangesCasesTableFrame {...tableFrameCommon} onSortRequest={handleRequestSort}>
                        <S.DataTableRow>
                            <S.DataTableCell
                                colSpan={tableColSpan}
                                style={{ border: 'none', verticalAlign: 'middle' }}
                            >
                                <S.LoadingContainer data-testid="flight-changes-refetch-spinner">
                                    <Spinner size="normal" />
                                    <S.LoadingText>{t('flightChangesCases.loadingRefetchMessage')}</S.LoadingText>
                                </S.LoadingContainer>
                            </S.DataTableCell>
                        </S.DataTableRow>
                    </FlightChangesCasesTableFrame>
                ) }
                { !error && !loading && flights.length > 0 && (
                    <>
                    <FlightChangesCasesTableFrame {...tableFrameCommon} onSortRequest={handleRequestSort}>
                        {flights.map((flight) => (
                            <S.DataTableRow
                                key={flight.flightChangeId}
                                $clickable
                                onClick={() => handleStatusClick(flight.flightChangeId)}
                                data-testid={`row-${flight.flightChangeId}`}
                            >
                                <S.DataTableCell>{renderRegistration(flight)}</S.DataTableCell>
                                <S.DataTableCell>
                                    <Text variant="body-md">{flight.awb}</Text>
                                </S.DataTableCell>
                                <S.DataTableCell>
                                    <Text variant="body-md">{flight.route}</Text>
                                </S.DataTableCell>
                                <S.DataTableCell>
                                    <Text variant="body-md">{flight.movimentNumber}</Text>
                                </S.DataTableCell>
                                <S.DataTableCell>{renderNotification(flight)}</S.DataTableCell>
                                <S.DataTableCell>
                                    <AssignmentButton
                                        analyst={flight.analyst}
                                        isAssigning={isAssigning(flight.flightChangeId)}
                                        onAssign={() => handleAssign(flight.flightChangeId, flight.awb || '')}
                                        showToAssignLink={false}
                                        isNotApplicable={isCaseReadOnly(flight.status)}
                                    />
                                </S.DataTableCell>
                                <S.DataTableCell>
                                    <StatusChip status={flight.status} />
                                </S.DataTableCell>
                                <S.DataTableCell $variant="checkbox">
                                    <S.ArrowIcon>
                                        <ChevronRightOutlined aria-hidden />
                                    </S.ArrowIcon>
                                </S.DataTableCell>
                            </S.DataTableRow>
                        ))}
                    </FlightChangesCasesTableFrame>
                    <S.PaginationToolbar>
                        <S.NotificationDeadlineLegend>
                            <S.NotificationDeadlineDot />
                            <S.NotificationLegendText><Text variant="label-xs">{t("flightChangesCases.notificationDeadlineToExpire")}</Text></S.NotificationLegendText>
                        </S.NotificationDeadlineLegend>
                        <FlightChangesCasesTablePagination
                            count={total}
                            page={page}
                            onPageChange={handleChangePage}
                            rowsPerPage={pageSize}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                            rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
                            labelRowsPerPage={t('flightChangesCases.rowsPerPage')}
                            labelDisplayedRows={({ from, to, count }) =>
                                `${from}-${to} ${t('flightChangesCases.of')} ${count}`
                            }
                        />
                    </S.PaginationToolbar>
                    </>
                ) }
            </S.Card>
        
        </S.Container>
        <S.Footer>
            <ErrorSnackbar open={!!conflictError} onClose={clearError}>
                <Text variant="body-md" bold={false}>
                    {t("flightChangesCases.assignConflict.message")}&nbsp;
                    <b>{conflictError?.awb}</b>&nbsp;
                    {t("flightChangesCases.assignConflict.messageSuffix")}
                </Text>
            </ErrorSnackbar>
            <ErrorSnackbar open={!!genericError} onClose={clearError}>
                <Text variant="body-md" bold={false}>
                    {genericError?.message}
                </Text>
            </ErrorSnackbar>
        </S.Footer>
        </>
    );
};
